import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import {
  getHouseholdModelPortfolioSecurities,
  groupHouseholdModelPortfolioSecuritiesByAssetClass,
  isHouseholdModelPortfolioAssetClassStrategy,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { isEqual, round, sumBy } from 'lodash-es';
import type { HouseholdModelPortfolioSectionSnapshot } from '../../workflowTypes';
import {
  getTaxRankValidation,
  validateGroupAllocations,
} from '../utils/useAssetClassGrouping';
import {
  type FormattedAssetClassBranch,
  type FormattedChild,
  formatAssetClassBranches,
  formatChildren,
  isBranchAllocationInvalid,
} from '../utils/useSaveModelPortfolio';

/** The id a row carries until a security is picked for it. */
const TEMP_ID_PREFIX = 'temp-';

/** A row the advisor added but has not yet chosen a security for. */
const isIncompleteRow = (row: Security): boolean =>
  !row.financialProduct.id ||
  row.financialProduct.id.startsWith(TEMP_ID_PREFIX);

/**
 * The household's model portfolio as the securities step leaves it.
 *
 * That step is shared with the shelf model flow and works in rows rather than
 * in the holdings a model is made of, so it writes its work alongside them.
 */
export type EditedHouseholdModelPortfolio = HouseholdModelPortfolioModel & {
  securities?: Security[];
};

const toChild = (
  security: Security,
  held?: HouseholdModelPortfolioChild,
): HouseholdModelPortfolioChild => ({
  ...held,
  percentage: security.percentage,
  isCashEquivalent:
    security.isCashEquivalent ?? held?.isCashEquivalent ?? false,
  taxRank: security.taxRank ?? held?.taxRank,
  assetClass: held?.assetClass ?? security.assetClass,
  // A security picked in the step brings the whole product with it, which is
  // what a holding the model never had has to be built out of.
  financialProduct: held?.financialProduct ?? {
    id: security.financialProduct.id,
    name: security.financialProduct.name,
    ticker: security.financialProduct.ticker ?? undefined,
    isCash: security.financialProduct.isCash,
    taxRank: security.financialProduct.taxRank ?? undefined,
    translatedName: security.financialProduct.translatedName,
    primaryAssetClass: security.financialProduct.primaryAssetClass,
    secondaryAssetClass: security.financialProduct.secondaryAssetClass,
    tertiaryAssetClass: security.financialProduct.tertiaryAssetClass,
  },
});

/**
 * The model as the advisor left it, in the shape everything downstream reads.
 *
 * The rows are the newer word on the holdings, so they replace them outright:
 * one dropped in the step is gone from the model, and the order the advisor put
 * them in is kept. What the model already knew about a holding it still has —
 * which asset class it sits in, and its own id — is carried across rather than
 * rebuilt from the row, which only knows what the table shows.
 *
 * A row left without a security is left out. It cannot be proposed, and the
 * weight it was holding is missing from the total either way, which is the
 * thing worth stopping on.
 */
export const withEditedSecurities = (
  model: EditedHouseholdModelPortfolio,
): HouseholdModelPortfolioModel => {
  const { securities, ...portfolio } = model;
  if (!securities) return portfolio;

  const heldByProduct = new Map(
    (portfolio.children ?? [])
      .filter((child) => !!child.financialProduct?.id)
      .map((child) => [child.financialProduct?.id as string, child]),
  );

  return {
    ...portfolio,
    children: securities
      .filter(
        (
          security,
        ): security is Security & { financialProduct: { id: string } } =>
          Boolean(
            security.financialProduct.id &&
              !security.financialProduct.id.startsWith(TEMP_ID_PREFIX),
          ),
      )
      .map((security) =>
        toChild(security, heldByProduct.get(security.financialProduct.id)),
      ),
  };
};

/**
 * What the model holds as things stand.
 *
 * Steps that only read the holdings — to total them against the bands, say —
 * want the rows folded in too, since a step that has been through leaves its
 * work there and not in the holdings themselves. Before that step, and on the
 * branches that never reach it, this is just what the model came with.
 */
export const editedHoldings = (
  model?: EditedHouseholdModelPortfolio,
): HouseholdModelPortfolioChild[] | undefined =>
  model ? withEditedSecurities(model).children : undefined;

/**
 * The allocation customization to send with the household's copy.
 *
 * Holdings and group weights are decided separately but sent together: within
 * a grouped model, product-service only checks that each group's holdings add
 * up when the holdings are part of the same request, so re-weighting a group
 * without restating what it holds would go in unchecked.
 */
export type AllocationOverrides = {
  children: FormattedChild[];
  /** Grouped models only — a flat model has no groups to re-weight. */
  assetClassBranches?: FormattedAssetClassBranch[];
};

/** The holdings as product-service reads them, from whichever side is asked. */
const asChildren = (
  children: HouseholdModelPortfolioChild[] | undefined,
  isAssetClass: boolean,
): FormattedChild[] =>
  formatChildren(
    getHouseholdModelPortfolioSecurities({
      children,
    } as HouseholdModelPortfolioModel),
    isAssetClass,
  );

/**
 * What the advisor changed about the parent model's allocations, or nothing.
 *
 * Selecting a parent stages that model's own holdings as table rows so the
 * steps downstream open on them rather than on an empty table. Those rows are
 * therefore not evidence of an edit, and sending them back would customize a
 * copy the advisor never touched — so they are compared against the parent that
 * was selected, and only a genuine difference is sent. A copy nobody edited
 * inherits the base model, which is what the Initialize flow has always meant.
 */
export const allocationOverrides = (
  draft: EditedHouseholdModelPortfolio,
  baseSnapshot?: HouseholdModelPortfolioSectionSnapshot,
): AllocationOverrides | undefined => {
  const securities = draft.securities;
  if (!securities?.length) return undefined;

  const isAssetClass = isHouseholdModelPortfolioAssetClassStrategy(draft);
  const children = formatChildren(securities, isAssetClass);

  // A row still waiting for a security is an edit part way through, not
  // something the parent model handed over, so it counts as a change even
  // though it is not yet a saveable one.
  const incomplete = securities.some(isIncompleteRow);

  const holdingsChanged =
    incomplete ||
    !baseSnapshot?.children?.length ||
    !isEqual(children, asChildren(baseSnapshot.children, isAssetClass));

  // Only a grouped model has groups to re-weight, and only a grouped model may
  // carry them: product-service refuses group weights on a flat master.
  const branches = isAssetClass
    ? formatAssetClassBranches(draft.assetClassBranches ?? [])
    : [];
  // No branches at all is a draft that never carried them rather than an
  // advisor clearing every group, so the base model's own are left to stand.
  const groupsChanged =
    branches.length > 0 &&
    !isEqual(
      branches,
      formatAssetClassBranches(baseSnapshot?.assetClassBranches ?? []),
    );

  if (!holdingsChanged && !groupsChanged) return undefined;

  return {
    children,
    ...(groupsChanged ? { assetClassBranches: branches } : {}),
  };
};

/** What stops the holdings from describing a model that can be proposed. */
export type HoldingsProblem =
  | 'EMPTY'
  | 'DUPLICATE'
  | 'WEIGHTS'
  | 'GROUP_HOLDINGS'
  | 'GROUP_WEIGHTS';

/**
 * What, if anything, stops these holdings from being proposed.
 *
 * These are the questions product-service asks of a proposal, asked here
 * instead: it only asks them when the proposal is sent for signature, by which
 * point the advisor has left the step where the weights are. A grouped model is
 * read by its group weights and is whole a group at a time, so the weights that
 * matter for it are not the ones that matter for a flat model.
 */
export const holdingsProblem = (
  model: HouseholdModelPortfolioModel,
): HoldingsProblem | undefined => {
  const securities = getHouseholdModelPortfolioSecurities(model);
  if (securities.length === 0) return 'EMPTY';

  const products = securities.map((security) => security.financialProduct.id);
  if (new Set(products).size !== products.length) return 'DUPLICATE';

  if (!isHouseholdModelPortfolioAssetClassStrategy(model)) {
    return round(sumBy(securities, 'percentage'), 2) === 100
      ? undefined
      : 'WEIGHTS';
  }

  // A grouped proposal is refused outright without them, since they are what
  // says how much of the model each group is answerable for.
  if (isBranchAllocationInvalid(model.assetClassBranches ?? [])) {
    return 'GROUP_WEIGHTS';
  }

  const { groupedSecurities, unassignedSecurities } =
    groupHouseholdModelPortfolioSecuritiesByAssetClass(
      true,
      securities,
      model.assetClassBranches ?? [],
      model.assetClassLevel,
    );

  return validateGroupAllocations(
    groupedSecurities ?? [],
    unassignedSecurities.length,
  )
    ? undefined
    : 'GROUP_HOLDINGS';
};

/** What stops Initialize from committing the allocations the advisor left. */
export type InitializeAllocationsProblem =
  | HoldingsProblem
  | 'INCOMPLETE'
  | 'DUPLICATE_TAX_RANK'
  | 'TAX_RANK_GAPS';

/**
 * What, if anything, stops the advisor initializing on these allocations.
 *
 * Only what the advisor decided here is held to this. Selecting a parent stages
 * that model's own holdings as rows for the table to open on, and a shelf model
 * saved below this standard is not something this step can fix — blocking on it
 * would strand the household on a screen with no way forward. So the same
 * comparison that decides whether anything is sent decides whether anything is
 * checked, and an untouched draft passes through to inherit the base model.
 */
export const initializeAllocationsProblem = (
  draft: EditedHouseholdModelPortfolio,
  baseSnapshot?: HouseholdModelPortfolioSectionSnapshot,
): InitializeAllocationsProblem | undefined => {
  const securities = draft.securities;
  // No rows staged at all is the editor never having written, rather than an
  // advisor having emptied the table.
  if (!securities) return undefined;

  // Emptying a table that came with holdings is a decision, and not one a copy
  // can be built from. A parent that had none itself is left to stand.
  if (securities.length === 0) {
    return baseSnapshot?.children?.length ? 'EMPTY' : undefined;
  }

  if (!allocationOverrides(draft, baseSnapshot)) return undefined;

  // Ahead of `holdingsProblem`, which reads the folded holdings and so never
  // sees a row still waiting for a security: without this, a blank row beside
  // rows already totalling 100% reads as clean and fails at the backend.
  if (securities.some(isIncompleteRow)) return 'INCOMPLETE';

  // Tax ranks are only sent for a grouped model, and product-service reads them
  // as one gap-free sequence from 1 across the whole model, not per group.
  if (isHouseholdModelPortfolioAssetClassStrategy(draft)) {
    const { hasDuplicateTaxRanks, hasGapsOrInvalidStart } =
      getTaxRankValidation(securities, true);
    if (hasDuplicateTaxRanks) return 'DUPLICATE_TAX_RANK';
    if (hasGapsOrInvalidStart) return 'TAX_RANK_GAPS';
  }

  return holdingsProblem(withEditedSecurities(draft));
};
